import * as React from 'react';
import { CustomButton, ButtonType, AlertMessage, AlertType, PaginationNavigation, SortTableContext } from "soprasteria-ui-library";
import { setMessage } from '../../../helpers/MessageHelper';
import { CreateInvoiceModal } from './CreateInvoiceModal/CreateInvoiceModal';
import { useAuth } from '../../../App';
import { SearchInvoiceApi } from '../../../api/SearchInvoiceApi';
import { ISearchInvoice, IInvoice, IInvoicePeriodStatus } from '../../../models/IInvoice';
import { IPagination } from 'soprasteria-ui-library/dist/models/IPagination';
import { SearchInvoiceTable } from "./SearchInvoice/SearchInvoiceTable";
import { SearchInvoiceForm } from './SearchInvoice/SearchInvoiceForm';
import { isInRole, UserContext } from '../../../contexts/UserContext';
import { ALLMÄNTANDVÅRDAKUTTANDVÅRDFAKTURERARE, TANDREGLERINGFAKTURERARE, ALLMÄNTANDVÅRDINTERCEPTIVAPPARATURFAKTURERARE, ALLMÄNTANDVÅRDFAKTURERARERISKPOTT, SPECIALISTTANDVÅRDFAKTURERARE, KÄKORTOPEDIFAKTURERARE, URVALSTANDLÄKAREFAKTURERARE } from '../../../utils/RoleConstants';
import { useHistory } from 'react-router';
import { dateTimeToDateString } from '../../../helpers/DateHelper';
import { SortOrder } from '../../../models/IPage';
import { INVOICES } from '../../../utils/RouteConstants';

export const Invoices: React.FunctionComponent<{}> = () => {

    const auth = useAuth();
    const searchInvoiceApi = new SearchInvoiceApi(auth);
    const [successMessage, setSuccessMessage] = React.useState<string>();
    const [renderForm, setRenderForm] = React.useState<boolean>(true);
    const [isFetchingInvoices, setIsFetchingInvoices] = React.useState<boolean>(false);
    const [invoices, setInvoices] = React.useState<IInvoice[]>();
    const [paginationHeader, setPaginationHeader] = React.useState<IPagination>();
    const pageSize = 20;
    const [searchParams, setSearchParams] = React.useState<ISearchInvoice>();
    const userInfo = React.useContext(UserContext);
    const hasRoleAllmäntandvårdAkuttandvårdFakturerare = isInRole(ALLMÄNTANDVÅRDAKUTTANDVÅRDFAKTURERARE, userInfo.roles);
    const hasRoleOrtodontisttandvårdFakturerare = isInRole(TANDREGLERINGFAKTURERARE, userInfo.roles);
    const hasRoleAllmäntandvårdInterceptivcheckFakturerare = isInRole(ALLMÄNTANDVÅRDINTERCEPTIVAPPARATURFAKTURERARE, userInfo.roles);
    const hasRoleAllmäntandvårdFHBFakturerare = isInRole(ALLMÄNTANDVÅRDFAKTURERARERISKPOTT, userInfo.roles);
    const hasRoleSpecialisttandvårdFakturerare = isInRole(SPECIALISTTANDVÅRDFAKTURERARE, userInfo.roles);
    const hasRoleLkgCfFakturerare = isInRole(KÄKORTOPEDIFAKTURERARE, userInfo.roles);
    const hasRoleKonsultRemissFakturerare = isInRole(URVALSTANDLÄKAREFAKTURERARE, userInfo.roles);
    const history = useHistory();
    const [invoicePeriodStatus, setInovicePeriodStatus] = React.useState<IInvoicePeriodStatus>();
    const { sortOrder, selectedPropertyToSortBy, setSelectedPropertyToSortBy, setSortOrder } = React.useContext(SortTableContext);
    const defaultOrderBy = "InvoiceDate";
    const abortController = new AbortController();
    const [isFirstSearch, setIsFirstSearch] = React.useState<boolean>(true);

    React.useEffect(() => {
        document.getElementById("clear-form")?.addEventListener("click", resetValue);

        if (setSelectedPropertyToSortBy && setSortOrder) {
            if (history.location.state !== undefined && history.location.state !== null) {
                const lastSearchParams = history.location.state as ISearchInvoice;
                setSelectedPropertyToSortBy(lastSearchParams.propertyToOrderBy ?? defaultOrderBy);
                lastSearchParams.sortOrder = lastSearchParams.sortOrder ?? SortOrder.descending;
                setSortOrder(lastSearchParams.sortOrder);
                searchInvoices(lastSearchParams);
            }
            else {
                setSelectedPropertyToSortBy(defaultOrderBy);
                setSortOrder(SortOrder.descending);
                searchInvoices({ pageSize: pageSize, pageNumber: 1, propertyToOrderBy: defaultOrderBy, sortOrder: SortOrder.descending });
            }
        }

        return () => {
            abortController.abort();
            document.getElementById("clear-form")?.removeEventListener("click", resetValue);
        }
    }, [])

    React.useEffect(() => {
        if (renderCreateInvoiceModal()) {
            getInvoicePeriodStatus();
        }
    }, [hasRoleAllmäntandvårdAkuttandvårdFakturerare, hasRoleOrtodontisttandvårdFakturerare, hasRoleAllmäntandvårdInterceptivcheckFakturerare, hasRoleAllmäntandvårdFHBFakturerare, hasRoleSpecialisttandvårdFakturerare, hasRoleLkgCfFakturerare])

    React.useEffect(() => {
        if (invoices && !isFirstSearch) {
            onSortOrderClick();
        }
    }, [selectedPropertyToSortBy, sortOrder])

    const resetValue = () => {
        history.push(INVOICES, undefined);
    }

    const changeSuccessMessage = (message: string) => {
        const newMessage = setMessage(message, successMessage ?? "");
        setSuccessMessage(newMessage);
    }

    const searchInvoices = (data: ISearchInvoice) => {
        setIsFetchingInvoices(true);
        data.pageSize = pageSize;
        data.pageNumber = data.pageNumber ?? 1;
        data.propertyToOrderBy = data.propertyToOrderBy ?? selectedPropertyToSortBy;
        data.sortOrder = data.sortOrder ?? sortOrder;
        history.push(INVOICES, data);

        searchInvoiceApi.searchInvoices(data, false, (response) => {
            setInvoices(response.data);
            setPaginationHeader(response.paginationHeader);
        }, undefined, undefined, () => setIsFetchingInvoices(false));
        setSearchParams(data);
        setIsFirstSearch(false);
    }

    const onPageSelect = (pageNumber: number) => {
        if (searchParams) {
            setIsFetchingInvoices(true);
            var currentSearchParams = searchParams;
            currentSearchParams.pageNumber = pageNumber;
            history.push(INVOICES, currentSearchParams);

            searchInvoiceApi.searchInvoices(currentSearchParams, false, (response) => {
                setInvoices(response.data);
                setPaginationHeader(response.paginationHeader);
            }, undefined, undefined, () => setIsFetchingInvoices(false));
        }
    }

    const onSortOrderClick = () => {
        if (searchParams) {
            setIsFetchingInvoices(true);
            var currentSearchParams = searchParams;
            currentSearchParams.pageNumber = 1;
            currentSearchParams.sortOrder = sortOrder;
            currentSearchParams.propertyToOrderBy = selectedPropertyToSortBy;
            history.push(INVOICES, currentSearchParams);

            searchInvoiceApi.searchInvoices(currentSearchParams, false, (response) => {
                setInvoices(response.data);
                setPaginationHeader(response.paginationHeader);
            }, undefined, undefined, () => setIsFetchingInvoices(false));
        }
    }

    const getInvoicePeriodStatus = () => {
        searchInvoiceApi.getInvoicePeriodStatus((data) => {
            setInovicePeriodStatus(data);
        });
    }

    const renderCreateInvoiceModal = () => {
        return hasRoleAllmäntandvårdAkuttandvårdFakturerare || hasRoleOrtodontisttandvårdFakturerare || hasRoleAllmäntandvårdInterceptivcheckFakturerare || hasRoleAllmäntandvårdFHBFakturerare || hasRoleSpecialisttandvårdFakturerare || hasRoleLkgCfFakturerare || hasRoleKonsultRemissFakturerare;
    }

    return (
        <section className="card" >
            <div className="card-header" >
                <h1>Fakturor</h1>
            </div>
            <div className="card-body">
                <AlertMessage alertType={AlertType.success} duration={3000}>{successMessage}</AlertMessage>
                <SearchInvoiceForm isFetchingInvoices={isFetchingInvoices} searchInvoices={searchInvoices} preSetSearchParams={history.location.state !== undefined && history.location.state !== null ? history.location.state as ISearchInvoice : undefined} changeSuccessMessage={changeSuccessMessage} />
                {
                    invoicePeriodStatus ?
                        <div className="panel text-right">
                            {
                                invoicePeriodStatus.isOpen
                                    ? <CustomButton buttonType={ButtonType.create} data-toggle="modal" data-target="#create-invoice-modal" buttonText="Skapa faktura" onClick={() => setRenderForm(true)} />
                                    : <CustomButton buttonType={ButtonType.create} disabled buttonText="Skapa faktura"
                                        title={
                                            invoicePeriodStatus.nextOpenPeriodDate
                                                ? `Bokslutsperioden är inte öppen. Nästa bokslutsperiod öppnar: ${dateTimeToDateString(invoicePeriodStatus.nextOpenPeriodDate)}.`
                                                : `Bokslutsperioden är inte öppen. Information saknas för när nästa bokslutsperiod öppnas.`
                                        } />
                            }
                        </div>
                        : null
                }
                {
                    invoices ?
                        <>
                            <SearchInvoiceTable invoices={invoices} />
                            <PaginationNavigation
                                ariaLabel="received invoices pagination selector"
                                pagination={paginationHeader}
                                pageNeighbours={2}
                                selectedPage={page => onPageSelect(page)}
                                showCount={true} />
                        </>
                        : null
                }
            </div>
            {
                renderCreateInvoiceModal() ?
                    <CreateInvoiceModal renderForm={renderForm} setRenderForm={setRenderForm} changeSuccessMessage={changeSuccessMessage} />
                    : null
            }
        </section>
    )
}

